/**
 * useRealtimeEvents — WebSocket client for real-time staff dashboard events.
 *
 * Connects to the WebSocket server, handles reconnection with exponential backoff,
 * and provides a subscription API for components to listen to specific event types.
 */
import { useEffect, useRef, useCallback, useState } from 'react';

export type WsEventType =
  | 'TRANSACTION_CLAIMED'
  | 'TRANSACTION_ASSIGNED'
  | 'TRANSACTION_APPROVED'
  | 'TRANSACTION_RETURNED'
  | 'TRANSACTION_DENIED'
  | 'TRANSACTION_ESCALATED'
  | 'COMPLIANCE_CLAIMED'
  | 'POOL_CHANGED'
  | 'CONNECTED'
  | 'PONG';

export interface WsEvent {
  type: WsEventType;
  transactionUUID?: string;
  payload?: Record<string, any>;
  timestamp: string;
}

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

type EventCallback = (event: WsEvent) => void;

const WS_URL = import.meta.env.VITE_WS_URL || '';
const WS_ENABLED = !!WS_URL && !WS_URL.includes('localhost');
const MAX_RECONNECT_DELAY = 30000;
const INITIAL_RECONNECT_DELAY = 1000;
const PING_INTERVAL = 25000;

export function useRealtimeEvents() {
  const wsRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef<Map<string, Set<EventCallback>>>(new Map());
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pingTimerRef = useRef<ReturnType<typeof setInterval>>();
  const mountedRef = useRef(true);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');

  const connect = useCallback(() => {
    if (!WS_ENABLED) return;
    if (!mountedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const token = localStorage.getItem('token');
    const url = token ? `${WS_URL}?token=${encodeURIComponent(token)}` : WS_URL;

    setConnectionState('connecting');

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) { ws.close(); return; }
        setConnectionState('connected');
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
        console.log('[WS] Connected');

        // Start ping keepalive
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const data: WsEvent = JSON.parse(event.data);

          // Notify type-specific listeners
          const typeListeners = listenersRef.current.get(data.type);
          if (typeListeners) {
            typeListeners.forEach((cb) => cb(data));
          }

          // Notify wildcard listeners
          const wildcardListeners = listenersRef.current.get('*');
          if (wildcardListeners) {
            wildcardListeners.forEach((cb) => cb(data));
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        if (pingTimerRef.current) clearInterval(pingTimerRef.current);
        if (!mountedRef.current) return;

        setConnectionState('reconnecting');
        console.log(`[WS] Disconnected. Reconnecting in ${reconnectDelayRef.current}ms...`);

        reconnectTimerRef.current = setTimeout(() => {
          reconnectDelayRef.current = Math.min(
            reconnectDelayRef.current * 2,
            MAX_RECONNECT_DELAY
          );
          connect();
        }, reconnectDelayRef.current);
      };

      ws.onerror = () => {
        // onclose will fire after onerror, which handles reconnection
      };
    } catch {
      setConnectionState('disconnected');
    }
  }, []);

  // Subscribe to a specific event type (or '*' for all events)
  const subscribe = useCallback(
    (eventType: WsEventType | '*', callback: EventCallback): (() => void) => {
      if (!listenersRef.current.has(eventType)) {
        listenersRef.current.set(eventType, new Set());
      }
      listenersRef.current.get(eventType)!.add(callback);

      // Return unsubscribe function
      return () => {
        listenersRef.current.get(eventType)?.delete(callback);
      };
    },
    []
  );

  // Connect on mount, clean up on unmount
  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect on intentional close
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { subscribe, connectionState };
}
